import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Crown } from 'lucide-react';
import { currentTenantOrNull } from '@/lib/tenants';
import { useTranslation } from 'react-i18next';
import { PublicFooter } from '@/components/PublicFooter';
import { useOrgProfile } from '@/lib/useOrgProfile';

// Story 076 — build-time branding defaults baked into the bundle
// (same vars the htmlBranding() Vite plugin substitutes into
// <title> / meta tags). When set, they let HomePage render the org
// name + tagline in the very first paint — important so pre-JS
// crawlers (Google OAuth brand verification) see the right text
// even before the org-profile fetch resolves. When unset (dev +
// multi-tenant test env), we fall back to gating on the live
// profile so a branded tenant doesn't flash "Swiss Magic".
const BUILD_APP_NAME = (import.meta.env.VITE_APP_NAME ?? '').trim();
const BUILD_APP_DESCRIPTION = (
  import.meta.env.VITE_APP_DESCRIPTION ?? ''
).trim();

export function HomePage() {
  const { t } = useTranslation('common');
  // Story 063 — white-label tenants disable self-registration; the
  // Register button shouldn't appear since the /register page itself
  // shows an "invitation only" message there.
  const tenant = currentTenantOrNull();
  const allowsSelfRegistration = tenant?.allowsSelfRegistration ?? true;
  const { profile, loading } = useOrgProfile();
  // Name precedence: live org profile > build-time default > i18n
  // generic. Each step is more specific than the next, so as the
  // live data arrives it just refines (never widens) what's shown.
  const displayName =
    profile?.name?.trim() || BUILD_APP_NAME || t('app.name');
  const displayTagline =
    profile?.description?.trim() ||
    BUILD_APP_DESCRIPTION ||
    t('app.tagline');
  const logoUrl = profile?.logoUrl ?? null;

  // Skip the loading gate when we have a build-time name — first
  // paint is already the right brand. Without one (dev / test env),
  // keep gating so a branded tenant doesn't flash defaults. The
  // logo always waits regardless: there's no static fallback for
  // the upload URL, and a Crown→logo swap is the original FOUC bug.
  const nameReady = BUILD_APP_NAME.length > 0 || !loading;
  const logoReady = !loading;

  return (
    <div
      data-testid="home-page"
      className="flex min-h-dvh flex-col items-center justify-center bg-background px-4 pb-[env(safe-area-inset-bottom)]"
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
        {logoReady &&
          (logoUrl ? (
            <img
              data-testid="org-logo"
              src={logoUrl}
              alt={displayName}
              className="max-h-28 max-w-[16rem] object-contain"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/20">
              <Crown className="h-8 w-8 text-accent-foreground" />
            </div>
          ))}
        {nameReady && (
          <div className="flex flex-col gap-2">
            <h1 className="text-4xl font-bold tracking-tight text-foreground">
              {displayName}
            </h1>
            <p className="text-lg text-muted-foreground">{displayTagline}</p>
          </div>
        )}
        {nameReady && (
          <div className="flex gap-3">
            {allowsSelfRegistration && (
              <Button asChild size="lg">
                <Link to="/register">{t('buttons.register')}</Link>
              </Button>
            )}
            <Button
              asChild
              size="lg"
              variant={allowsSelfRegistration ? 'outline' : 'default'}
            >
              <Link to="/login">{t('buttons.sign_in')}</Link>
            </Button>
          </div>
        )}
      </div>
      <PublicFooter />
    </div>
  );
}
